import { getFullSelectedIds } from '@aibees/elephant/feature/search-table-company/utils';
import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { useCreateCompanyIdsSearchListBasedOnPeopleIds } from '@aibees/elephant/hooks/company/use-create-company-ids-search-list-based-on-people-ids';
import { useHandleFindButton } from '@aibees/elephant/hooks/use-handle-find-button';
import { SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { useFullScreenLoadingContext } from '@aibees/shared/context';
import { useGetBrowseIdsApiData } from '@aibees/shared/features/advance-table/components/table';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { FindButtonProps as Props } from './types';

export const FindButton = memo(
  ({ className, disabled, isSmall, table }: Props) => {
    const type = useSearchTypeFromRoute();
    const {
      setTitle: setLoadingScreenTitle,
      state: loadingScreenState,
      reset: resetLoadingScreen,
    } = useFullScreenLoadingContext();
    const title =
      type === SearchType.People
        ? 'Find Companies which the selected people are working in.'
        : 'Find People that are working in the selected companies.';
    const getSelectedIdsApi = useGetBrowseIdsApiData(table);
    const enableGetCompanyIdsSearchListBasedOnPeopleIdsApi = useBoolean(false);
    const [peopleSearchListId, setPeopleSearchListId] = useState<string>();
    const createCompanyIdsSearchListBasedOnPeopleIdsApi =
      useCreateCompanyIdsSearchListBasedOnPeopleIds({
        searchListId: peopleSearchListId,
        enabled: enableGetCompanyIdsSearchListBasedOnPeopleIdsApi.value,
      });
    const selectedIds = useMemo(() => {
      return getFullSelectedIds(getSelectedIdsApi?.data ?? [], table);
    }, [getSelectedIdsApi?.data, table]);

    const {
      createSearchListAndRedirect,
      onlyRedirectToSearchList,
      createTempPeopleSearchList,
    } = useHandleFindButton({
      type,
    });

    const isDisabled =
      disabled ||
      getSelectedIdsApi?.isFetching ||
      (type === SearchType.People &&
        createCompanyIdsSearchListBasedOnPeopleIdsApi.isFetching);

    useEffect(() => {
      if (
        type === SearchType.People &&
        !!createCompanyIdsSearchListBasedOnPeopleIdsApi.data &&
        enableGetCompanyIdsSearchListBasedOnPeopleIdsApi.value
      ) {
        enableGetCompanyIdsSearchListBasedOnPeopleIdsApi.onFalse();
        onlyRedirectToSearchList(
          createCompanyIdsSearchListBasedOnPeopleIdsApi.data.id,
          createCompanyIdsSearchListBasedOnPeopleIdsApi.data.records ?? 0
        );
      } else if (createCompanyIdsSearchListBasedOnPeopleIdsApi.isError) {
        enableGetCompanyIdsSearchListBasedOnPeopleIdsApi.onFalse();
        resetLoadingScreen();
      }
    }, [
      type,
      createCompanyIdsSearchListBasedOnPeopleIdsApi.data,
      createCompanyIdsSearchListBasedOnPeopleIdsApi.isError,
      onlyRedirectToSearchList,
      enableGetCompanyIdsSearchListBasedOnPeopleIdsApi,
      resetLoadingScreen,
    ]);

    const onClick = useCallback(() => {
      loadingScreenState.onTrue();
      setLoadingScreenTitle('Redirecting...');
      if (type !== SearchType.People) {
        createSearchListAndRedirect(selectedIds);
      }
      if (type === SearchType.People) {
        createTempPeopleSearchList(selectedIds, (payload) => {
          setPeopleSearchListId(payload.id);
          enableGetCompanyIdsSearchListBasedOnPeopleIdsApi.onTrue();
        });
      }
    }, [
      type,
      selectedIds,
      createSearchListAndRedirect,
      loadingScreenState,
      setLoadingScreenTitle,
      createTempPeopleSearchList,
      enableGetCompanyIdsSearchListBasedOnPeopleIdsApi,
    ]);

    return (
      <Styled.Wrapper
        className={classnames('FindButton', className)}
        sx={SX.root}
        title={isDisabled ? title : undefined}
        placement="top"
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={
            createCompanyIdsSearchListBasedOnPeopleIdsApi.isFetching ? (
              <Icon name="loading" size={18} />
            ) : (
              <Icon
                name={
                  type === SearchType.People ? 'business' : 'people-outline'
                }
                size={18}
              />
            )
          }
          onClick={onClick}
          disabled={isDisabled}
        >
          {!isSmall &&
            (type === SearchType.People ? 'Find Companies' : 'Find People')}
        </Button>
      </Styled.Wrapper>
    );
  }
);

FindButton.displayName = 'FindButton';
